import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Rectangle,
} from "recharts";
import { AppProvider } from "../../ContextApi/AppProvider";
import { PedidoService } from "../../Services/PedidoService";
import { RelatorioPedidosPorMes } from "../Shared/Interfaces/RelatorioPedidosPorMes";
import { FiltroRelatorio } from "../Shared/Interfaces/Pedido";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DefaultLoader from "../Shared/DefaultLoader";
import colors from "../../theme/colors";
import { formatDate } from "../../Utils/utils";

// Configurar locale pt-BR para dayjs
dayjs.locale("pt-br");

const CORES_TIPO_GRAFICO = [
  "#2E7D32",
  "#FF6F00",
  "#7B1FA2",
  "#00838F",
  "#C62828",
  "#5D4037",
  "#1565C0",
];

function formatarLabelTipo(tipo: string): string {
  return tipo
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function coletarTiposProduto(items: RelatorioPedidosPorMes[]): string[] {
  const set = new Set<string>();
  for (const item of items) {
    const q = item.quantidadePorTipoProduto;
    if (q && typeof q === "object") {
      Object.keys(q).forEach((k) => set.add(k));
    }
  }
  return Array.from(set).sort();
}

function entradasQuantidadePorTipo(
  item: RelatorioPedidosPorMes
): [string, number][] {
  const q = item.quantidadePorTipoProduto;
  if (q && typeof q === "object" && Object.keys(q).length > 0) {
    return Object.entries(q).sort(([a], [b]) => a.localeCompare(b));
  }
  const fallback: [string, number][] = [];
  if (item.quantidadePneus > 0) fallback.push(["PNEU", item.quantidadePneus]);
  if (item.quantidadeOutros > 0) fallback.push(["OUTROS", item.quantidadeOutros]);
  return fallback;
}

function quantidadeTipoNoItem(item: RelatorioPedidosPorMes, tipo: string): number {
  const q = item.quantidadePorTipoProduto;
  if (q && tipo in q && q[tipo] != null) return Number(q[tipo]) || 0;
  if (!q || Object.keys(q).length === 0) {
    const t = tipo.toUpperCase();
    if (t === "PNEU" || t === "PNEUS") return item.quantidadePneus || 0;
    if (t === "OUTROS" || t === "OUTRO") return item.quantidadeOutros || 0;
  }
  return 0;
}

export default function RelatorioPedidosPorMesComponent() {
  const contextApi = useContext(AppProvider);
  const pedidoService = new PedidoService();
  const [loading, setLoading] = useState(false);
  const [mesAnoInicial, setMesAnoInicial] = useState<Dayjs>(
    dayjs().startOf("month")
  );
  const [mesAnoFinal, setMesAnoFinal] = useState<Dayjs>(dayjs().endOf("month"));
  const [relatorioPedidosPorMes, setRelatorioPedidosPorMes] = useState<
    RelatorioPedidosPorMes[]
  >([]);

  const PesquisarPor = () => {
    setLoading(true);
    // Garantir que a data inicial seja o primeiro dia do mês e a final seja o último dia do mês
    const dataInicial = mesAnoInicial != null ? mesAnoInicial.startOf("month") : null;
    const dataFinal = mesAnoFinal != null ? mesAnoFinal.endOf("month") : null;
    
    const filtroPesquisa: FiltroRelatorio = {
      dataInicial: dataInicial != null ? formatDate(dataInicial) : null,
      dataFinal: dataFinal != null ? formatDate(dataFinal) : null,
    };

    pedidoService
      .gerarRelatorioPedidosPorMes(filtroPesquisa)
      .then((response) => {
        if (response.status === 200) {
          // Garantir que sempre seja um array
          const data = Array.isArray(response.data) ? response.data : [];
          setRelatorioPedidosPorMes(data);
        } else if (response.status === 204) {
          // Limpar os cards e mostrar mensagem
          setRelatorioPedidosPorMes([]);
          contextApi.showMessage("Nenhum dado encontrado para o período selecionado", true, "info");
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        // Verificar se é status 204
        if (error.response?.status === 204) {
          setRelatorioPedidosPorMes([]);
          contextApi.showMessage("Nenhum dado encontrado para o período selecionado", true, "info");
        } else {
          const errorMessage = error.response?.data || "Erro ao gerar relatório";
          contextApi.showMessage(errorMessage, true, "error");
          // Em caso de erro, garantir que seja um array vazio
          setRelatorioPedidosPorMes([]);
        }
      });
  };

  // Função para formatar valor monetário
  const formatarValor = (valor: number | null): string => {
    if (valor === null || valor === undefined) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  };

  // Função para traduzir nome do mês para português
  const traduzirMes = (mes: string): string => {
    const mesesMap: { [key: string]: string } = {
      "january": "Janeiro",
      "february": "Fevereiro",
      "march": "Março",
      "april": "Abril",
      "may": "Maio",
      "june": "Junho",
      "july": "Julho",
      "august": "Agosto",
      "september": "Setembro",
      "october": "Outubro",
      "november": "Novembro",
      "december": "Dezembro",
      "jan": "Janeiro",
      "feb": "Fevereiro",
      "mar": "Março",
      "apr": "Abril",
      "jun": "Junho",
      "jul": "Julho",
      "aug": "Agosto",
      "sep": "Setembro",
      "oct": "Outubro",
      "nov": "Novembro",
      "dec": "Dezembro",
    };

    const mesLower = mes.toLowerCase();
    return mesesMap[mesLower] || mes.charAt(0).toUpperCase() + mes.slice(1).toLowerCase();
  };

  // Processar dados para o gráfico (barras por tipo vêm de quantidadePorTipoProduto quando existir)
  const processarDadosParaGrafico = (): {
    rows: Record<string, string | number>[];
    tipos: string[];
  } => {
    if (!Array.isArray(relatorioPedidosPorMes) || !relatorioPedidosPorMes.length) {
      return { rows: [], tipos: [] };
    }

    const dadosOrdenados = [...relatorioPedidosPorMes].sort(
      (a, b) => a.idMes - b.idMes
    );
    const tipos = coletarTiposProduto(dadosOrdenados);

    const rows = dadosOrdenados.map((item) => {
      const row: Record<string, string | number> = {
        mes: traduzirMes(item.mes),
        "Quantidade de Pedidos": item.quantidadePedidos,
        "Quantidade de Itens": item.quantidadeItens,
      };
      if (tipos.length > 0) {
        tipos.forEach((tipo) => {
          row[tipo] = quantidadeTipoNoItem(item, tipo);
        });
      } else {
        row.Pneus = item.quantidadePneus ?? 0;
        row.Outros = item.quantidadeOutros ?? 0;
      }
      return row;
    });

    return { rows, tipos };
  };

  // Componente de tooltip personalizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataItem = payload[0]?.payload;
      // Buscar o item original comparando o mês traduzido
      const itemOriginal = relatorioPedidosPorMes.find(
        (item) => traduzirMes(item.mes).toLowerCase() === label.toLowerCase()
      );

      return (
        <Paper sx={{ p: 2, boxShadow: 3, bgcolor: 'white' }}>
          <Typography variant="h6" sx={{ color: colors.bluePrimary, mb: 1 }}>
            📅 {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: entry.color }}>
                {entry.name}: {entry.value}
              </Typography>
            </Box>
          ))}
          {itemOriginal && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: colors.bluePrimary }}>
                Pedidos: {itemOriginal.quantidadePedidos}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                Valor Total: {formatarValor(itemOriginal.valorTotal)}
              </Typography>
            </Box>
          )}
        </Paper>
      );
    }
    return null;
  };

  const { rows: dadosGrafico, tipos: tiposGrafico } = processarDadosParaGrafico();

  const dadosArray = Array.isArray(relatorioPedidosPorMes) ? relatorioPedidosPorMes : [];
  const totalPedidos = dadosArray.reduce((sum, item) => sum + (item.quantidadePedidos || 0), 0);
  const totalItens = dadosArray.reduce((sum, item) => sum + (item.quantidadeItens || 0), 0);
  const valorTotal = dadosArray.reduce((sum, item) => sum + (item.valorTotal || 0), 0);

  const tiposResumo = coletarTiposProduto(dadosArray);
  const cardsTotaisPorTipo: { tipo: string; total: number }[] =
    tiposResumo.length > 0
      ? tiposResumo.map((tipo) => ({
          tipo,
          total: dadosArray.reduce(
            (sum, item) => sum + quantidadeTipoNoItem(item, tipo),
            0
          ),
        }))
      : [
          {
            tipo: "PNEU",
            total: dadosArray.reduce((s, i) => s + (i.quantidadePneus || 0), 0),
          },
          {
            tipo: "OUTROS",
            total: dadosArray.reduce((s, i) => s + (i.quantidadeOutros || 0), 0),
          },
        ].filter((c) => c.total > 0);

  React.useEffect(() => {
    PesquisarPor();
  }, []);

  // Função para obter cor do card baseado no mês - Estilo neutro e clean
  const obterCorCard = (idMes: number): string => {
    // Paleta neutra em tons de cinza e azul muito suave
    const cores = [
      "#f5f5f5", // Cinza muito claro
      "#e8e8e8", // Cinza claro
      "#f5f5f5", // Cinza muito claro
      "#e8e8e8", // Cinza claro
      "#f5f5f5", // Cinza muito claro
      "#e8e8e8", // Cinza claro
      "#f5f5f5", // Cinza muito claro
      "#e8e8e8", // Cinza claro
      "#f5f5f5", // Cinza muito claro
      "#e8e8e8", // Cinza claro
      "#f5f5f5", // Cinza muito claro
      "#e8e8e8", // Cinza claro
    ];
    return cores[(idMes - 1) % cores.length];
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Filtros */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>🗓️ Filtros de Período</h4>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <DemoContainer components={["DatePicker"]}>
                      <DatePicker
                        label="Mês/Ano Inicial"
                        defaultValue={mesAnoInicial}
                        value={mesAnoInicial}
                        views={["month", "year"]}
                        format="MM/YYYY"
                        onChange={(newValue) =>
                          setMesAnoInicial(newValue ?? mesAnoInicial)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                          },
                        }}
                      />
                    </DemoContainer>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DemoContainer components={["DatePicker"]}>
                      <DatePicker
                        value={mesAnoFinal}
                        onChange={(newValue) =>
                          setMesAnoFinal(newValue ?? mesAnoFinal)
                        }
                        label="Mês/Ano Final"
                        views={["month", "year"]}
                        format="MM/YYYY"
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                          },
                        }}
                      />
                    </DemoContainer>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.5}>
                    <Button
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{ height: "40px", width: "100%" }}
                      onClick={() => PesquisarPor()}
                    >
                      {loading ? (
                        <DefaultLoader
                          height="2.5em"
                          width="2.5em"
                          colors={[
                            "#1876D2",
                            "#1876D2",
                            "#1876D2",
                            "#1876D2",
                            "#1876D2",
                          ]}
                          textInfo="Gerando Relatório..."
                        />
                      ) : (
                        "Pesquisar"
                      )}
                    </Button>
                  </Grid>
              </Grid>
              </LocalizationProvider>
            </Paper>
          </Grid>

          {/* Linha 2 - Cards de Resumo */}
          {Array.isArray(relatorioPedidosPorMes) && relatorioPedidosPorMes.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 1, mb: 1 }}>
                <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>
                  📊 Resumo Geral - {mesAnoInicial.format("MM/YYYY")} a {mesAnoFinal.format("MM/YYYY")}
                </h4>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "#f8f9fa",
                        borderRadius: 2,
                        color: "text.primary",
                        boxShadow: 1,
                        border: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: "bold", mb: 0.5, color: colors.bluePrimary }}
                      >
                        {totalPedidos}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Total de Pedidos
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "#f8f9fa",
                        borderRadius: 2,
                        color: "text.primary",
                        boxShadow: 1,
                        border: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: "bold", mb: 0.5, color: colors.bluePrimary }}
                      >
                        {totalItens}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Total de Itens
                      </Typography>
                    </Paper>
                  </Grid>
                  {cardsTotaisPorTipo.map(({ tipo, total }, idx) => (
                    <Grid item xs={12} sm={6} md={2} key={tipo}>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: "#f8f9fa",
                          borderRadius: 2,
                          color: "text.primary",
                          boxShadow: 1,
                          border: "1px solid #e0e0e0",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: "bold",
                            mb: 0.5,
                            color: CORES_TIPO_GRAFICO[idx % CORES_TIPO_GRAFICO.length],
                          }}
                        >
                          {total}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {formatarLabelTipo(tipo)}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={2}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "#f8f9fa",
                        borderRadius: 2,
                        color: "text.primary",
                        boxShadow: 1,
                        border: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", mb: 0.5, color: colors.bluePrimary }}
                      >
                        {formatarValor(valorTotal)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Valor Total
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Linha 3 - Cards dos Meses */}
          {Array.isArray(relatorioPedidosPorMes) && relatorioPedidosPorMes.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 1, mb: 1 }}>
                <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📅 Relatório por Mês</h4>
                <Grid container spacing={2}>
                  {relatorioPedidosPorMes
                    .sort((a, b) => a.idMes - b.idMes)
                    .map((item) => {
                      const porTipoMes = entradasQuantidadePorTipo(item);
                      return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={item.idMes}>
                        <Paper 
                          sx={{ 
                            p: 2, 
                            bgcolor: obterCorCard(item.idMes), 
                            borderRadius: 2, 
                            color: "text.primary",
                            boxShadow: 1,
                            border: '1px solid #e0e0e0',
                            minHeight: '200px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: colors.bluePrimary }}>
                              {traduzirMes(item.mes)}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                              <strong>Pedidos:</strong> {item.quantidadePedidos}
                              {" · "}
                              <strong>Itens:</strong> {item.quantidadeItens}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: "block", mb: 0.75, color: "text.secondary" }}
                            >
                              Por tipo de produto
                            </Typography>
                            <Grid container spacing={1} sx={{ mb: 1 }}>
                              {porTipoMes.map(([tipo, qtd], idx) => (
                                <Grid item xs={6} key={tipo}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1,
                                      textAlign: "center",
                                      bgcolor: "rgba(255,255,255,0.7)",
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "bold",
                                        lineHeight: 1.2,
                                        color:
                                          CORES_TIPO_GRAFICO[idx % CORES_TIPO_GRAFICO.length],
                                      }}
                                    >
                                      {qtd}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatarLabelTipo(tipo)}
                                    </Typography>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                            {porTipoMes.length === 0 && (
                              <Typography variant="caption" sx={{ color: "text.secondary", mb: 1 }}>
                                Nenhuma quantidade por tipo neste mês
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                mt: "auto",
                                pt: 1,
                                fontWeight: "bold",
                                fontSize: "0.9rem",
                                color: colors.bluePrimary,
                              }}
                            >
                              {formatarValor(item.valorTotal)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      );
                    })}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Linha 4 - Gráfico */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📊 Gráfico de Pedidos por Mês</h4>
              
              <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                {dadosGrafico.length > 0 ? (
                  <Box sx={{ height: '500px', width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={dadosGrafico}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        barCategoryGap="20%"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="mes" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          fontSize={12}
                        />
                        <YAxis 
                          label={{ 
                            value: 'Quantidade', 
                            angle: -90, 
                            position: 'insideLeft',
                            fontSize: 12
                          }}
                        />
                        <Tooltip content={CustomTooltip} />
                        <Legend 
                          verticalAlign="top" 
                          height={36}
                          wrapperStyle={{ paddingBottom: '10px' }}
                        />
                        
                        <Bar
                          dataKey="Quantidade de Pedidos"
                          name="Quantidade de Pedidos"
                          fill="#1976D2"
                          activeBar={<Rectangle fill="#1976D2" stroke="#1C1C1C" />}
                        />
                        {tiposGrafico.length > 0 ? (
                          tiposGrafico.map((tipo, idx) => (
                            <Bar
                              key={tipo}
                              dataKey={tipo}
                              name={formatarLabelTipo(tipo)}
                              fill={
                                CORES_TIPO_GRAFICO[idx % CORES_TIPO_GRAFICO.length]
                              }
                              activeBar={
                                <Rectangle
                                  fill={
                                    CORES_TIPO_GRAFICO[idx % CORES_TIPO_GRAFICO.length]
                                  }
                                  stroke="#1C1C1C"
                                />
                              }
                            />
                          ))
                        ) : (
                          <>
                            <Bar
                              dataKey="Pneus"
                              name="Pneus"
                              fill="#2E7D32"
                              activeBar={<Rectangle fill="#2E7D32" stroke="#1C1C1C" />}
                            />
                            <Bar
                              dataKey="Outros"
                              name="Outros"
                              fill="#FF6F00"
                              activeBar={<Rectangle fill="#FF6F00" stroke="#1C1C1C" />}
                            />
                          </>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Nenhum dado disponível para o período selecionado
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

